import { BREAKPOINT_INFINITY } from 'common/constants/cap-table/capTable';
import { largeCurrencyFormat } from 'common/formats/formats';
import { Currency } from 'common/types/format';
import { formatNumbers } from 'utilities';
import { BreakpointColumn } from '../types/breakpoint-analysis.models';

type BreakpointSerie = BreakpointColumn['series'][number];

export const getBreakpointColumnTotal = (column: BreakpointColumn) =>
  column.series.reduce((acc, serie) => acc + Number(serie.value), 0) || 0;

const formatBreakpointTotal = (value: number, currency: Currency): string =>
  String(
    formatNumbers({
      value,
      format: largeCurrencyFormat,
      currency,
    } as any)
  );

export const getBreakpointLowerLimit = ({
  columnIndex,
  columns,
  alphabet,
  currencySymbol,
  currency,
}: {
  columnIndex: number;
  columns: BreakpointColumn[];
  alphabet: string[];
  currencySymbol: string;
  currency: Currency;
}): string => {
  if (columnIndex === 0) {
    return `${currencySymbol}0`;
  }
  const prevColumn = columns[columnIndex - 1];
  if (prevColumn.series.every(serie => !serie.value)) {
    return `${currencySymbol}${alphabet[columnIndex]}`;
  }
  return formatBreakpointTotal(getBreakpointColumnTotal(prevColumn), currency);
};

export const getBreakpointUpperLimit = ({
  columnIndex,
  columns,
  alphabet,
  currencySymbol,
  currency,
}: {
  columnIndex: number;
  columns: BreakpointColumn[];
  alphabet: string[];
  currencySymbol: string;
  currency: Currency;
}): string => {
  if (columnIndex === columns.length - 1) {
    return BREAKPOINT_INFINITY;
  }
  const column = columns[columnIndex];
  if (column.series.every(serie => !serie.value)) {
    return `${currencySymbol}${alphabet[columnIndex + 1]}`;
  }
  return formatBreakpointTotal(getBreakpointColumnTotal(column), currency);
};

export const getBreakpointRange = ({
  columnIndex,
  columns,
  alphabet,
  currencySymbol,
  currency,
}: {
  columnIndex: number;
  columns: BreakpointColumn[];
  alphabet: string[];
  currencySymbol: string;
  currency: Currency;
}): string => {
  const lowerLimit = getBreakpointLowerLimit({
    columnIndex,
    columns,
    alphabet,
    currencySymbol,
    currency,
  });
  const upperLimit = getBreakpointUpperLimit({
    columnIndex,
    columns,
    alphabet,
    currencySymbol,
    currency,
  });
  return `${lowerLimit} to ${upperLimit}`;
};

// Keep every column's series aligned with the template (rowConfig source of truth)
export const alignSeriesToTemplate = (
  seriesTemplate: BreakpointSerie[],
  sourceSeries: BreakpointSerie[],
  { resetValues = false }: { resetValues?: boolean } = {}
): BreakpointSerie[] => {
  if (resetValues) {
    return seriesTemplate.map(serie => ({ ...serie, value: 0 }));
  }
  const seriesById = new Map(sourceSeries.map(serie => [serie.id, serie]));
  return seriesTemplate.map(serie => {
    const existing = seriesById.get(serie.id);
    return existing ? { ...existing } : { ...serie, value: 0 };
  });
};
